import { useId, useRef, type ReactNode } from 'react';

/**
 * Option cards with a radio (design/MieteAnpassen "Neuer Betrag", design/Abo "Zahlungsart", design/KontoDaten
 * "Was soll passieren?", design/Mahnung "Versand"). Native radios: arrow keys move, one tab stop.
 */
export type RadioCardOption<V extends string> = { value: V; title: ReactNode; sub?: ReactNode; aside?: ReactNode; disabled?: boolean };
export function RadioCards<V extends string>({ label, labelHidden, options, value, onChange, columns = 1, name }: { label: string; labelHidden?: boolean; options: RadioCardOption<V>[]; value: V | null; onChange: (v: V) => void; columns?: 1 | 2 | 3; name?: string }) {
  const id = useId();
  const group = name ?? id;
  return (
    <fieldset className="radio-cards-set">
      <legend className={labelHidden ? 'sr-only' : 'radio-cards-legend'}>{label}</legend>
      <div className={`radio-cards cols-${columns}`}>
        {options.map((o) => (
          <label key={o.value} className={`radio-card ${o.value === value ? 'selected' : ''} ${o.disabled ? 'disabled' : ''}`}>
            <input type="radio" name={group} value={o.value} checked={o.value === value} disabled={o.disabled} onChange={() => onChange(o.value)} />
            <span className="rc-mark" aria-hidden />
            <span className="rc-text">
              <span className="rc-title">{o.title}</span>
              {o.sub && <span className="rc-sub">{o.sub}</span>}
            </span>
            {o.aside != null && <span className="rc-aside">{o.aside}</span>}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/** On/off switch with title and explanation (design/MieterEinladen "Zahlungen freigeben"). */
export function Switch({ label, text, checked, onChange, disabled, boxed }: { label: ReactNode; text?: ReactNode; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; boxed?: boolean }) {
  const id = useId();
  return (
    <div className={`switch-row ${boxed ? 'boxed' : ''}`}>
      <span className="switch-text">
        <label htmlFor={id} className="switch-label">{label}</label>
        {text && <span className="switch-sub" id={`${id}-sub`}>{text}</span>}
      </span>
      <input id={id} type="checkbox" role="switch" className="switch" checked={checked} disabled={disabled} aria-describedby={text ? `${id}-sub` : undefined} onChange={(e) => onChange(e.target.checked)} />
    </div>
  );
}

/** Segmented choice whose options carry a small status badge (design/Mahnung "Erinnerung 15.09. · 1. Mahnung · 2. Mahnung jetzt"). */
export function StagePicker<V extends string>({ label, options, value, onChange }: { label: string; options: { value: V; label: string; badge?: ReactNode; disabled?: boolean }[]; value: V; onChange: (v: V) => void }) {
  return (
    <div className="stage-picker" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button key={o.value} type="button" role="radio" aria-checked={o.value === value} disabled={o.disabled} onClick={() => onChange(o.value)}>
          <span>{o.label}</span>
          {o.badge != null && <span className="stage-badge">{o.badge}</span>}
        </button>
      ))}
    </div>
  );
}

/** One-time code in single-digit boxes (design/Sicherheit "6-stelligen Code eingeben"); paste fills all boxes. */
export function CodeInput({ label, length = 6, value, onChange, state, message, disabled }: { label: string; length?: number; value: string; onChange: (v: string) => void; state?: 'ok' | 'err'; message?: ReactNode; disabled?: boolean }) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  const id = useId();
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');
  const set = (i: number, d: string) => {
    const next = digits.slice();
    next[i] = d;
    onChange(next.join('').replace(/\s/g, '').slice(0, length));
  };
  const fill = (text: string, from: number) => {
    const clean = text.replace(/\D/g, '').slice(0, length - from);
    if (!clean) return;
    const next = digits.slice();
    [...clean].forEach((c, k) => (next[from + k] = c));
    onChange(next.join('').slice(0, length));
    refs.current[Math.min(from + clean.length, length - 1)]?.focus();
  };
  return (
    <div className="field">
      <span className="label" id={id}>{label}</span>
      <div className={`code-input ${state ?? ''}`} role="group" aria-labelledby={id} aria-describedby={message ? `${id}-msg` : undefined}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            className="code-box"
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            disabled={disabled}
            value={d}
            aria-label={`${label} ${i + 1}/${length}`}
            aria-invalid={state === 'err' || undefined}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, '');
              if (v.length > 1) return fill(v, i);
              set(i, v);
              if (v && i < length - 1) refs.current[i + 1]?.focus();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
              if (e.key === 'ArrowRight' && i < length - 1) refs.current[i + 1]?.focus();
            }}
            onPaste={(e) => { e.preventDefault(); fill(e.clipboardData.getData('text'), i); }}
          />
        ))}
      </div>
      {message && <span id={`${id}-msg`} className={state === 'err' ? 'err' : state === 'ok' ? 'code-ok' : 'hint'} role={state ? 'status' : undefined}>{message}</span>}
    </div>
  );
}
